import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'

import { Caption, H6, Subtitle1 } from '@src/elements/typography/typography'
import { ProgressBar } from '@src/elements/progress-bar/progress-bar'
import { ProgressBarCircle } from '@src/elements/progress-bar-circle/progress-bar-circle'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Bullet } from '@src/elements/bullet/bullet'
import { availableEquipment, NO_EQUIPMENT } from '@src/util/constants'
import { SwipeItemList, SwipeList } from '@src/components/swipe-list'

import './index.sass'
import { ExerciseTagFilter } from '@src/state/store'

@Component({
  computed: {
    exerciseFilterTags: {
      get: function () {
        return this.$store.state.exerciseFilters.tags
      }
    },
    exerciseTags () {
      return this.$store.state.exerciseTags
    }
  }
})
export default class PortraitComposition extends Vue {

  @Prop({ required: false, default: {} })
  progress: { [key: string]: number }

  @Prop({ required: true })
  title: string

  @Prop({ required: false })
  filterCallback: (exerciseFilterTags: ExerciseTagFilter) => void

  @Prop({ required: false, default: 215 })
  bodyCircleDiameter: number

  @Prop({ required: false, default: 86 })
  muscleCircleDiameter: number

  @Prop({ required: false, default: 86 })
  movementCircleDiameter: number

  @Prop({ required: false, default: true })
  isFilterEnabled: boolean

  @Prop({ required: false, default: false })
  showLegend: boolean

  private exerciseTags: any

  private exerciseFilterTags: ExerciseTagFilter

  private bodyAreaVisible: boolean = true
  private executionTabVisible: boolean = true

  constructor () {
    super()
  }

  toggleExecutionTab () {
    this.executionTabVisible = !this.executionTabVisible
  }

  isFilterSelected (tag, type) {
    return (this.exerciseFilterTags[type] || new Set([])).has(tag)
  }

  filterByTag (tag: string, type: string) {
    this.exerciseFilterTags[type] = (this.exerciseFilterTags[type] || new Set([])).add(tag)
    this.filterCallback(this.exerciseFilterTags)
  }

  removeFilter (tag: string, type: string) {
    (this.exerciseFilterTags[type] || new Set([])).delete(tag)

    this.filterCallback(this.exerciseFilterTags)
  }

  updateBodyAreaVisibilityOnMobile () {
    this.bodyAreaVisible = !this.bodyAreaVisible
  }

  getItemsByPage (items: HTMLElement[], itemsCountOnPage: number) {
    const result: SwipeItemList[] = []
    let swipeItemList = new SwipeItemList({ content: [] })
    items.forEach((item, index) => {
      if (index && index % itemsCountOnPage === 0) {
        result.push(swipeItemList)
        swipeItemList = new SwipeItemList({ content: [] })
      }
      swipeItemList.content.push({ ...item })
      if (index === items.length - 1) {
        result.push(swipeItemList)
      }
    })

    return result
  }

  render (h: CreateElement) {

    if (!this.exerciseTags) {
      return
    }

    const plusTooltip = this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.SUGGEST_TAG')
    const minusTooltip = this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.REMOVE_TAG')
    let muscleGroups = this.exerciseTags['ExerciseTags::MuscleGroup'] ? this.exerciseTags['ExerciseTags::MuscleGroup'].map(muscle => {
      return <div class={'tag-progress '}>
        <div class='progress-wrapper'>

          <ProgressBarCircle ref={`tag-progress-${muscle}`} progress={0} image={'/assets/icon/muscle/' + muscle + '.svg'}
                           diameter={this.muscleCircleDiameter} strokeWidth={1} strokeColor={'#707070'} />
          <div class='progress-line-wrapper'>
            {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + muscle.toUpperCase())}
            <ProgressBar ref={`tag-progress-${muscle}-line`} progress={this.progress[muscle] || 0} />
          </div>
        </div>
      </div>
    }) : []

    let movementPatterns = this.exerciseTags['ExerciseTags::MovementPattern'] ? this.exerciseTags['ExerciseTags::MovementPattern'].map(movement => {
      const isFilterSelected = this.isFilterSelected(movement, 'movement')
      return <Caption class={'focus-item ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + movement.toUpperCase())}
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(movement, 'movement')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(movement, 'movement')} /></span>
          </Tooltip>}
        <ProgressBar ref={`tag-progress-${movement}`} progress={this.progress[movement] || 0} />
      </Caption>
    }) : []

    let focus = this.exerciseTags['ExerciseTags::Focus'] ? this.exerciseTags['ExerciseTags::Focus'].map(focusItem => {
      const isFilterSelected = this.isFilterSelected(focusItem, 'focus')
      return <Caption class={'focus-item ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + focusItem.toUpperCase())}
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(focusItem, 'focus')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(focusItem, 'focus')} /></span>
          </Tooltip>}
        <ProgressBar ref={`tag-progress-${focusItem}`} progress={this.progress[focusItem] || 0} />
      </Caption>
    }) : []

    let bodyAreaText = this.exerciseTags['ExerciseTags::BodyArea'] ? this.exerciseTags['ExerciseTags::BodyArea'].map((body) => {
      return <Caption class={'body-filter-tag '}>
        {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + body.toUpperCase())}
        <br />
      </Caption>
    }) : []

    let executionItems = this.exerciseTags['ExerciseTags::Execution'] ? this.exerciseTags['ExerciseTags::Execution'].map(exec => {
      return <div class={'tag-progress'}>
        <div class='progress-wrapper'>
          <ProgressBarCircle ref={`tag-progress-${exec}`} progress={0} image={'/assets/icon/execution/' + exec.toLowerCase() + '.svg'}
                             diameter={this.muscleCircleDiameter} strokeWidth={1} strokeColor={'#707070'} />
          <div class='progress-line-wrapper'>
            {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + exec.toUpperCase())}
            <ProgressBar ref={`tag-progress-${exec}-line`} progress={this.progress[exec] || 0} />
          </div>
        </div>
      </div>
    }) : []

    let categoryItems = this.exerciseTags['ExerciseTags::Category'] ? this.exerciseTags['ExerciseTags::Category'].map(category => {
      return <div class={'tag-progress'}>
        <div class='progress-wrapper'>
          <ProgressBarCircle ref={`tag-progress-${category}`} progress={0} image={'/assets/icon/category/' + category.toLowerCase() + '.svg'}
                             diameter={this.muscleCircleDiameter} strokeWidth={1} strokeColor={'#707070'} />
          <div class='progress-line-wrapper'>
            {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + category.toUpperCase())}
            <ProgressBar ref={`tag-progress-${category}-line`} progress={this.progress[category] || 0} />
          </div>
        </div>
      </div>
    }) : []

    let equipmentList = [NO_EQUIPMENT, ...availableEquipment].map(eq => {
      const isFilterSelected = this.isFilterSelected(eq, 'equipment')
      const imgPath = eq === NO_EQUIPMENT ? '/assets/icon/util/close.svg' : '/assets/icon/equipment/' + eq + '.svg'
      return <div class={'tag-progress ' + (isFilterSelected ? 'tag-progress-selected' : '')}>
        {isFilterSelected ?
          <Tooltip content={minusTooltip}>
            <span><span class='filter-btn minus' onClick={() => this.removeFilter(eq, 'equipment')} /></span>
          </Tooltip> :
          <Tooltip content={plusTooltip}>
            <span><span class='filter-btn plus' onClick={() => this.filterByTag(eq, 'equipment')} /></span>
          </Tooltip>}
        <div class='progress-wrapper'>

          <ProgressBarCircle ref={`tag-progress-${eq}`} progress={0} image={imgPath} strokeColor={'#4FACFE'}
                             diameter={this.muscleCircleDiameter} strokeWidth={1} />
          <div class='progress-line-wrapper'>
            {this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.' + eq.toUpperCase())}
            <ProgressBar ref={`tag-progress-${eq}-line`} progress={this.progress[eq] || 0} />
          </div>
        </div>

      </div>
    })

    // standard diameters will be: 180 145 110 75
    let bodyAreaCirclesDiameter = this.bodyCircleDiameter
    let bodyAreas = this.exerciseTags['ExerciseTags::BodyArea'] ? this.exerciseTags['ExerciseTags::BodyArea'].map((body) => {
      bodyAreaCirclesDiameter -= 35
      return <ProgressBarCircle strokeWidth={6} progress={this.progress[body] || 0} diameter={bodyAreaCirclesDiameter} class='combined-circle' croppedProgress={true} />
    }) : []

    // to be used for printing style
    const isSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1

    const equipmentsByPage = this.getItemsByPage(equipmentList, 4)
    const muscleItemsByPage = this.getItemsByPage(muscleGroups, 4)
    const executionItemsByPage = this.getItemsByPage(executionItems, 4)
    const categoryItemsByPage = this.getItemsByPage(categoryItems, 4)

    return <div class={`portrait-workout-composition ${isSafari ? 'isSafari' : ''}`}>
      <H6 class='column-header title'>
        {this.$t(this.title)}
      </H6>

      <div class='row visible-on-tablet'>
        <div class='body-area-wrapper'>
          <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.BODY_PLACEHOLDER')}</Subtitle1>
          <div class='body-area'>
            <div class='combined-circle-wrapper'>
              <div class='body-filters'>
                {bodyAreaText}
              </div>
              <div class='body-areas'>{bodyAreas}</div>
            </div>
          </div>
          {this.showLegend ?
            <Caption class='workout-composition-legend'>
              <Bullet color='cyan' />{this.$t('HIGH')}
              <Bullet color='yellow' />{this.$t('MODERATE')}
              <Bullet color='red' />{this.$t('LOW')}
              <Bullet color='grey' />{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.NOT_TRAINED')}
            </Caption> : null}
        </div>
        {this.isFilterEnabled && <div class='focus'>
          <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.FOCUS_PLACEHOLDER')}</Subtitle1>
          <div class='focus-progress composition-wrapper'>
            {focus}
          </div>
        </div>}
      </div>

      <div class='row column-on-mobile visible-on-mobile'>
        {this.isFilterEnabled ? (
          <div class='labels-tab'>
            <div class={`label-tab ${this.bodyAreaVisible && 'active'}`} onClick={this.updateBodyAreaVisibilityOnMobile}>
              <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.BODY_PLACEHOLDER')}</Subtitle1>
            </div>
            <div class={`label-tab ${!this.bodyAreaVisible && 'active'}`} onClick={this.updateBodyAreaVisibilityOnMobile}>
              <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.FOCUS_PLACEHOLDER')}</Subtitle1>
            </div>
          </div>
        ) : (
          <div class='labels-tab'>
            <div class='label-tab active'>
              <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.BODY_PLACEHOLDER')}</Subtitle1>
            </div>
          </div>
        )}
        <div class='tab-content-wrapper' style={{ 'border-top-left-radius': (!this.isFilterEnabled || this.bodyAreaVisible) ? 0 : '4vmin' } }>
          {(this.bodyAreaVisible || !this.isFilterEnabled) &&
            <div class='body-area-wrapper'>
              <div class='body-area'>
                <div class='combined-circle-wrapper'>
                  <div class='body-filters'>
                    {bodyAreaText}
                  </div>
                  <div class='body-areas'>{bodyAreas}</div>
                </div>
              </div>
              {this.showLegend ?
                <Caption class='workout-composition-legend'>
                  <Bullet color='cyan' />{this.$t('HIGH')}
                  <Bullet color='yellow' />{this.$t('MODERATE')}
                  <Bullet color='red' />{this.$t('LOW')}
                  <Bullet color='grey' />{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.NOT_TRAINED')}
                </Caption> : null}
            </div>
          }
          {this.isFilterEnabled && !this.bodyAreaVisible &&
            <div class='focus-progress composition-wrapper'>
              {focus}
            </div>
          }
        </div>
      </div>

      <div class='row column-on-mobile'>
        <div class='row-item'>
          <SwipeList
            items={muscleItemsByPage}
            title={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.MUSCLE_PLACEHOLDER')}
          />
        </div>
        {this.isFilterEnabled && <div class='row-item'>
          <SwipeList
            items={equipmentsByPage}
            title={this.$t('EQUIPMENT')}
          />
        </div>}
      </div>

      <div class='row visible-on-tablet'>
        <div class='row-item'>
          <SwipeList
            items={executionItemsByPage}
            title={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.EXECUTION_PLACEHOLDER')}
          />
        </div>
        <div class='row-item'>
          <SwipeList
            items={categoryItemsByPage}
            title={this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.CATEGORY_PLACEHOLDER')}
          />
        </div>
      </div>

      <div class='row column-on-mobile visible-on-mobile'>
        <div class='labels-tab'>
          <div class={`label-tab ${this.executionTabVisible ? 'active' : ''}`} onClick={this.toggleExecutionTab}>
            <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.EXECUTION_PLACEHOLDER')}</Subtitle1>
          </div>
          <div class={`label-tab ${!this.executionTabVisible ? 'active' : ''}`} onClick={this.toggleExecutionTab}>
            <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.CATEGORY_PLACEHOLDER')}</Subtitle1>
          </div>
        </div>
        <div class='tab-content-wrapper' style={{ 'border-top-left-radius': this.executionTabVisible ? 0 : '4vmin' }}>
          {this.executionTabVisible &&
            <div class='execution-tab-content' key='execution'>
              {executionItems}
            </div>
          }
          {!this.executionTabVisible &&
            <div class='category-tab-content' key='category'>
              {categoryItems}
            </div>
          }
        </div>
      </div>

      {/* only rendered with filters enabled, so the disabled class never applies here */}
      {this.isFilterEnabled && <div class={'composition' + (!this.isFilterEnabled ? /* istanbul ignore next */ ' filter-disabled' : '')}>
        <div class='movement-pattern'>
          <Subtitle1>{this.$t('PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.MOVEMENT_PLACEHOLDER')}</Subtitle1>
          <div class='focus-progress composition-wrapper member-movement-patterns'>
            {movementPatterns}
          </div>
        </div>
      </div>}
    </div>
  }
}
